import React, { Fragment, useRef, useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle, Description, Transition } from '@headlessui/react';
import CodeEditor from '@uiw/react-textarea-code-editor';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid';
import type {
  IDatabaseConfig,
  ISchemaConfig,
  ITableConfig,
  IViewConfig,
  IColumnConfig,
  IProcedureConfig,
  IFunctionConfig,
  IFileFormatConfig,
  ISequenceConfig,
  IPartialObjectConfig,
  IObjectType,
  IManageMode,
} from '../utils/configEditorTypes';
import GrantEditor from './GrantEditor';
import DropdownSingleselect from 'components/DropdownSingleselect';
import FormField from 'components/FormField';

export interface IEditorModalConfig {
  action: 'add' | 'edit';
  objectType:
    | 'database'
    | 'schema'
    | 'table'
    | 'view'
    | 'procedure'
    | 'function'
    | 'file_format'
    | 'sequence'
    | 'column'
    | undefined;
  objectData:
    | IDatabaseConfig
    | ISchemaConfig
    | ITableConfig
    | IViewConfig
    | IProcedureConfig
    | IFunctionConfig
    | IFileFormatConfig
    | ISequenceConfig
    | IColumnConfig
    | IPartialObjectConfig;
}

// Component for the "View documentation" link for Manage Mode
const ManageModeDocsLink: React.FC = () => {
  return (
    <a
      href="https://docs.dataops.live/docs/sole/user-guide/namespace-environment-management/#manage-mode"
      target="_blank"
      rel="noopener noreferrer"
      className="absolute text-xs text-dataops-primary hover:text-dataops-primary-hover top-[4.5px] left-[105px] flex items-center"
    >
      View docs
      <ArrowTopRightOnSquareIcon className="w-3 h-3 ml-0.5" />
    </a>
  );
};

interface ConfigEditorModalProps {
  open: boolean;
  onClose: () => void;
  config: IEditorModalConfig | undefined;
  onAddObject: (
    objectType: 'schema' | 'table' | 'view' | 'procedure' | 'function' | 'file_format' | 'sequence' | 'column',
    objectData:
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
  ) => void;
  onEditObject: (
    objectType:
      | 'database'
      | 'schema'
      | 'table'
      | 'view'
      | 'procedure'
      | 'function'
      | 'file_format'
      | 'sequence'
      | 'column',
    objectData:
      | IDatabaseConfig
      | ISchemaConfig
      | ITableConfig
      | IViewConfig
      | IProcedureConfig
      | IFunctionConfig
      | IFileFormatConfig
      | ISequenceConfig
      | IColumnConfig,
    originalName: string,
  ) => void;
}

export default function ConfigEditorModal(props: ConfigEditorModalProps): JSX.Element {
  const cancelButtonRef = useRef(null);

  const [databaseConfig, setDatabaseConfig] = useState<IDatabaseConfig | null>(null);
  const [schemaConfig, setSchemaConfig] = useState<ISchemaConfig | null>(null);
  const [tableConfig, setTableConfig] = useState<ITableConfig | null>(null);
  const [viewConfig, setViewConfig] = useState<IViewConfig | null>(null);
  const [procedureConfig, setProcedureConfig] = useState<IProcedureConfig | null>(null);
  const [functionConfig, setFunctionConfig] = useState<IFunctionConfig | null>(null);
  const [fileFormatConfig, setFileFormatConfig] = useState<IFileFormatConfig | null>(null);
  const [sequenceConfig, setSequenceConfig] = useState<ISequenceConfig | null>(null);
  const [columnConfig, setColumnConfig] = useState<IColumnConfig | null>(null);

  // For object type selection when adding a new object
  const [objectType, setObjectType] = useState<IObjectType | undefined>(undefined);

  useEffect(() => {
    if (props.config?.objectType !== undefined) {
      setObjectType(props.config.objectType);
    } else {
      setObjectType(undefined);
    }
  }, [props.config?.objectType]);

  useEffect(() => {
    if (props.config?.action === 'add' && objectType !== undefined && props.config.objectData !== undefined) {
      // Check if objectData is a column with all required fields
      if (
        objectType === 'column' &&
        'schema' in props.config.objectData &&
        'database' in props.config.objectData &&
        'table' in props.config.objectData
      ) {
        // If objectData already has the column fields, use it directly
        setColumnConfig(props.config.objectData);
      }
      // Check if objectData already has all the required fields for the schema
      else if (objectType === 'schema' && 'name' in props.config.objectData) {
        // If objectData already has the schema fields, use it directly
        setSchemaConfig(props.config.objectData as ISchemaConfig);
      }
      // Initialize the correct object data based on the selected object type
      // when objectData is of type IPartialObjectConfig
      else if ('schema' in props.config.objectData && 'database' in props.config.objectData) {
        const partialConfig = props.config.objectData as IPartialObjectConfig;
        const database = partialConfig.database;
        const schema = partialConfig.schema;

        switch (objectType) {
          case 'schema':
            setSchemaConfig({
              name: '',
              database,
              is_managed: false,
              is_transient: false,
              manage_mode: 'none',
              grants: {},
            });
            break;
          case 'table':
            setTableConfig({
              name: '',
              schema,
              database,
              is_managed: false,
              is_transient: false,
              manage_mode: 'none',
              columns: [],
              grants: {},
            });
            break;
          case 'view':
            setViewConfig({
              name: '',
              schema,
              database,
              is_managed: false,
              is_transient: false,
              manage_mode: 'none',
              statement: '',
              grants: {},
            });
            break;
          case 'procedure':
            setProcedureConfig({
              name: '',
              schema,
              database,
              return_type: '',
              arguments: {},
              execute_as: '',
              language: '',
              manage_mode: 'none',
              statement: '',
              grants: {},
            });
            break;
          case 'function':
            setFunctionConfig({
              name: '',
              schema,
              database,
              return_type: '',
              arguments: {},
              language: '',
              manage_mode: 'none',
              handler: '',
              statement: '',
              grants: {},
            });
            break;
          case 'file_format':
            setFileFormatConfig({
              name: '',
              schema,
              database,
              format_type: '',
              comment: '',
              manage_mode: 'none',
              grants: {},
            });
            break;
          case 'sequence':
            setSequenceConfig({
              name: '',
              schema,
              database,
              increment: 1,
              manage_mode: 'none',
              ordering: '',
              grants: {},
            });
            break;
          case 'column':
            setColumnConfig({
              name: '',
              schema,
              database,
              table: '',
              type: '',
              nullable: true,
            });
            break;
          default:
            break;
        }
      }
    }
  }, [objectType, props.config?.objectData, props.config?.action]);

  useEffect(() => {
    if (props.config?.action === 'edit' && props.config?.objectData !== undefined) {
      switch (objectType) {
        case 'database':
          setDatabaseConfig(props.config?.objectData as IDatabaseConfig);
          break;
        case 'schema':
          setSchemaConfig(props.config?.objectData as ISchemaConfig);
          break;
        case 'table':
          setTableConfig(props.config?.objectData as ITableConfig);
          break;
        case 'view':
          setViewConfig(props.config?.objectData as IViewConfig);
          break;
        case 'procedure':
          setProcedureConfig(props.config?.objectData as IProcedureConfig);
          break;
        case 'function':
          setFunctionConfig(props.config?.objectData as IFunctionConfig);
          break;
        case 'file_format':
          setFileFormatConfig(props.config?.objectData as IFileFormatConfig);
          break;
        case 'sequence':
          setSequenceConfig(props.config?.objectData as ISequenceConfig);
          break;
        case 'column':
          setColumnConfig(props.config?.objectData as IColumnConfig);
          break;
        default:
          break;
      }
    }
  }, [props.open, props.config?.objectData, props.config?.action, objectType]);

  const DatabaseFields = (
    <>
      {objectType === 'database' && databaseConfig !== null && (
        <>
          <div>
            <FormField
              label="Database Name"
              value={databaseConfig.name}
              onChange={(e) => setDatabaseConfig({ ...databaseConfig, name: e.target.value })}
              required={true}
            />
          </div>

          <div>
            <FormField
              label="Comment"
              value={databaseConfig.comment}
              onChange={(e) => setDatabaseConfig({ ...databaseConfig, comment: e.target.value })}
            />
          </div>

          <DropdownSingleselect
            label="Namespacing"
            options={['none', 'both', 'prefix', 'suffix', 'external']}
            selected={databaseConfig.namespacing}
            setSelected={(value) =>
              setDatabaseConfig({
                ...databaseConfig,
                namespacing: value as 'none' | 'both' | 'prefix' | 'suffix' | 'external',
              })
            }
          />

          <div className="relative">
            <DropdownSingleselect
              label="Manage Mode"
              options={['all', 'grants', 'none']}
              selected={databaseConfig.manage_mode}
              setSelected={(value) =>
                setDatabaseConfig({
                  ...databaseConfig,
                  manage_mode: value as IManageMode,
                })
              }
            />
            <ManageModeDocsLink />
          </div>

          <div className="col-span-2">
            <GrantEditor
              grants={databaseConfig.grants}
              setGrants={(updatedGrants) => setDatabaseConfig({ ...databaseConfig, grants: updatedGrants })}
            />
          </div>
        </>
      )}
    </>
  );

  const SchemaFields = (
    <>
      {objectType === 'schema' && schemaConfig !== null && (
        <>
          <div>
            <FormField
              label="Schema Name"
              value={schemaConfig.name}
              onChange={(e) => setSchemaConfig({ ...schemaConfig, name: e.target.value })}
              required={true}
            />
          </div>

          <div className="relative">
            <DropdownSingleselect
              label="Manage Mode"
              options={['all', 'grants', 'none']}
              selected={schemaConfig.manage_mode}
              setSelected={(value) =>
                setSchemaConfig({
                  ...schemaConfig,
                  manage_mode: value as IManageMode,
                })
              }
            />
            <ManageModeDocsLink />
          </div>

          <div className="col-span-2">
            <GrantEditor
              grants={schemaConfig.grants}
              setGrants={(updatedGrants) => setSchemaConfig({ ...schemaConfig, grants: updatedGrants })}
            />
          </div>
        </>
      )}
    </>
  );

  const TableFields = (
    <>
      {objectType === 'table' && tableConfig !== null && (
        <>
          <div>
            <FormField
              label="Table Name"
              value={tableConfig.name}
              onChange={(e) => setTableConfig({ ...tableConfig, name: e.target.value })}
              required={true}
            />
          </div>

          <div className="relative">
            <DropdownSingleselect
              label="Manage Mode"
              options={['all', 'grants', 'none']}
              selected={tableConfig.manage_mode}
              setSelected={(value) =>
                setTableConfig({
                  ...tableConfig,
                  manage_mode: value as IManageMode,
                })
              }
            />
            <ManageModeDocsLink />
          </div>

          <div className="col-span-2">
            <GrantEditor
              grants={tableConfig.grants}
              setGrants={(updatedGrants) => setTableConfig({ ...tableConfig, grants: updatedGrants })}
            />
          </div>
        </>
      )}
    </>
  );

  const ViewFields = (
    <>
      {objectType === 'view' && viewConfig !== null && (
        <>
          <div>
            <FormField
              label="View Name"
              value={viewConfig.name}
              onChange={(e) => setViewConfig({ ...viewConfig, name: e.target.value })}
              required={true}
            />
          </div>

          <div className="relative">
            <DropdownSingleselect
              label="Manage Mode"
              options={['all', 'grants', 'none']}
              selected={viewConfig.manage_mode}
              setSelected={(value) =>
                setViewConfig({
                  ...viewConfig,
                  manage_mode: value as IManageMode,
                })
              }
            />
            <ManageModeDocsLink />
          </div>

          <div className="col-span-2">
            <label htmlFor="statement" className="block text-sm font-medium leading-6 text-gray-900">
              Statement
            </label>
            <div className="mt-2">
              <div className="rounded-md border border-[#e2e8f0] bg-[#f8fafc] p-[2px] pl-[1.8rem]">
                <CodeEditor
                  id="statement"
                  name="statement"
                  language="sql"
                  minHeight={100}
                  data-color-mode="light"
                  value={viewConfig.statement}
                  onChange={(e) => setViewConfig({ ...viewConfig, statement: e.target.value })}
                  className="bg-transparent rounded-r block w-full border-0 text-sm text-black placeholder:text-gray-400 sql-code-editor"
                  style={{ overflow: 'visible', backgroundColor: '#ffffff' }}
                />
              </div>
            </div>
          </div>
          <div className="col-span-2">
            <GrantEditor
              grants={viewConfig.grants}
              setGrants={(updatedGrants) => setViewConfig({ ...viewConfig, grants: updatedGrants })}
            />
          </div>
        </>
      )}
    </>
  );

  const ColumnFields = (
    <>
      {objectType === 'column' && columnConfig !== null && (
        <>
          <div>
            <FormField
              label="Column Name"
              value={columnConfig.name}
              onChange={(e) => setColumnConfig({ ...columnConfig, name: e.target.value })}
              required={true}
            />
          </div>

          <div>
            <FormField
              label="Column Type"
              value={columnConfig.type}
              onChange={(e) => setColumnConfig({ ...columnConfig, type: e.target.value })}
              required={true}
            />
          </div>

          <div className="mt-4 flex items-center">
            <input
              id="nullable"
              type="checkbox"
              checked={columnConfig.nullable}
              onChange={(e) => setColumnConfig({ ...columnConfig, nullable: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
            />
            <label htmlFor="nullable" className="ml-2 block text-sm font-medium leading-6 text-gray-900">
              Nullable
            </label>
          </div>
        </>
      )}
    </>
  );

  const ProcedureFields = (
    <>
      {procedureConfig !== null && (
        <>
          <div>
            <FormField
              label="Procedure Name"
              value={procedureConfig.name}
              onChange={(e) => setProcedureConfig({ ...procedureConfig, name: e.target.value })}
              required={true}
            />
          </div>

          <div>
            <FormField
              label="Return Type"
              value={procedureConfig.return_type}
              onChange={(e) => setProcedureConfig({ ...procedureConfig, return_type: e.target.value })}
            />
          </div>

          <div>
            <FormField
              label="Execute As"
              value={procedureConfig.execute_as}
              onChange={(e) => setProcedureConfig({ ...procedureConfig, execute_as: e.target.value })}
            />
          </div>

          <div>
            <FormField
              label="Language"
              value={procedureConfig.language}
              onChange={(e) => setProcedureConfig({ ...procedureConfig, language: e.target.value })}
            />
          </div>

          <div className="relative">
            <DropdownSingleselect
              label="Manage Mode"
              options={['none', 'all']}
              selected={procedureConfig.manage_mode}
              setSelected={(value: string) =>
                setProcedureConfig({
                  ...procedureConfig,
                  manage_mode: value as IManageMode,
                })
              }
            />
            <ManageModeDocsLink />
          </div>

          <div className="col-span-2">
            <label htmlFor="statement" className="block text-sm font-medium leading-6 text-gray-900">
              Statement
            </label>
            <div className="mt-2">
              <div className="rounded-md border border-[#e2e8f0] bg-[#f8fafc] p-[2px] pl-[1.8rem]">
                <CodeEditor
                  id="statement"
                  name="statement"
                  language="sql"
                  minHeight={100}
                  data-color-mode="light"
                  value={procedureConfig.statement}
                  onChange={(e) => setProcedureConfig({ ...procedureConfig, statement: e.target.value })}
                  className="bg-transparent rounded-r block w-full border-0 text-sm text-black placeholder:text-gray-400 sql-code-editor"
                  style={{ overflow: 'visible', backgroundColor: '#ffffff' }}
                />
              </div>
            </div>
          </div>

          <div className="col-span-2">
            <GrantEditor
              grants={procedureConfig.grants}
              setGrants={(updatedGrants) => setProcedureConfig({ ...procedureConfig, grants: updatedGrants })}
            />
          </div>
        </>
      )}
    </>
  );

  const FunctionFields = (
    <>
      {functionConfig !== null && (
        <>
          <div>
            <FormField
              label="Function Name"
              value={functionConfig.name}
              onChange={(e) => setFunctionConfig({ ...functionConfig, name: e.target.value })}
              required={true}
            />
          </div>

          <div>
            <FormField
              label="Return Type"
              value={functionConfig.return_type}
              onChange={(e) => setFunctionConfig({ ...functionConfig, return_type: e.target.value })}
            />
          </div>

          <div>
            <FormField
              label="Language"
              value={functionConfig.language}
              onChange={(e) => setFunctionConfig({ ...functionConfig, language: e.target.value })}
            />
          </div>

          <div>
            <FormField
              label="Handler"
              value={functionConfig.handler}
              onChange={(e) => setFunctionConfig({ ...functionConfig, handler: e.target.value })}
            />
          </div>

          <div className="relative">
            <DropdownSingleselect
              label="Manage Mode"
              options={['none', 'all']}
              selected={functionConfig.manage_mode}
              setSelected={(value) =>
                setFunctionConfig({
                  ...functionConfig,
                  manage_mode: value as 'none' | 'all',
                })
              }
            />
            <ManageModeDocsLink />
          </div>

          <div className="col-span-2">
            <label htmlFor="statement" className="block text-sm font-medium leading-6 text-gray-900">
              Statement
            </label>
            <div className="mt-2">
              <div className="rounded-md border border-[#e2e8f0] bg-[#f8fafc] p-[2px] pl-[1.8rem]">
                <CodeEditor
                  id="statement"
                  name="statement"
                  language="sql"
                  minHeight={100}
                  data-color-mode="light"
                  value={functionConfig.statement}
                  onChange={(e) => setFunctionConfig({ ...functionConfig, statement: e.target.value })}
                  className="bg-transparent rounded-r block w-full border-0 text-sm text-black placeholder:text-gray-400 sql-code-editor"
                  style={{ overflow: 'visible', backgroundColor: '#ffffff' }}
                />
              </div>
            </div>
          </div>

          <div className="col-span-2">
            <GrantEditor
              grants={functionConfig.grants}
              setGrants={(updatedGrants) => setFunctionConfig({ ...functionConfig, grants: updatedGrants })}
            />
          </div>
        </>
      )}
    </>
  );

  const FileFormatFields = (
    <>
      {fileFormatConfig !== null && (
        <>
          <div>
            <FormField
              label="File Format Name"
              value={fileFormatConfig.name}
              onChange={(e) => setFileFormatConfig({ ...fileFormatConfig, name: e.target.value })}
              required={true}
            />
          </div>

          <div>
            <FormField
              label="Format Type"
              value={fileFormatConfig.format_type}
              onChange={(e) => setFileFormatConfig({ ...fileFormatConfig, format_type: e.target.value })}
              required={true}
            />
          </div>

          <div>
            <FormField
              label="Comment"
              value={fileFormatConfig.comment}
              onChange={(e) => setFileFormatConfig({ ...fileFormatConfig, comment: e.target.value })}
            />
          </div>

          <div className="relative">
            <DropdownSingleselect
              label="Manage Mode"
              options={['none', 'all']}
              selected={fileFormatConfig.manage_mode}
              setSelected={(value) =>
                setFileFormatConfig({
                  ...fileFormatConfig,
                  manage_mode: value as 'none' | 'all',
                })
              }
            />
            <ManageModeDocsLink />
          </div>

          <div className="col-span-2">
            <GrantEditor
              grants={fileFormatConfig.grants}
              setGrants={(updatedGrants) => setFileFormatConfig({ ...fileFormatConfig, grants: updatedGrants })}
            />
          </div>
        </>
      )}
    </>
  );

  const SequenceFields = (
    <>
      {sequenceConfig !== null && (
        <>
          <div>
            <FormField
              label="Sequence Name"
              value={sequenceConfig.name}
              onChange={(e) => setSequenceConfig({ ...sequenceConfig, name: e.target.value })}
              required={true}
            />
          </div>

          <div>
            <FormField
              label="Increment"
              value={sequenceConfig.increment === undefined ? '' : sequenceConfig.increment.toString()}
              onChange={(e) => setSequenceConfig({ ...sequenceConfig, increment: parseInt(e.target.value) })}
            />
          </div>

          <div>
            <FormField
              label="Ordering"
              value={sequenceConfig.ordering}
              onChange={(e) => setSequenceConfig({ ...sequenceConfig, ordering: e.target.value })}
            />
          </div>

          <div className="relative">
            <DropdownSingleselect
              label="Manage Mode"
              options={['none', 'all']}
              selected={sequenceConfig.manage_mode}
              setSelected={(value) =>
                setSequenceConfig({
                  ...sequenceConfig,
                  manage_mode: value as 'none' | 'all',
                })
              }
            />
            <ManageModeDocsLink />
          </div>

          <div className="col-span-2">
            <GrantEditor
              grants={sequenceConfig.grants}
              setGrants={(updatedGrants) => setSequenceConfig({ ...sequenceConfig, grants: updatedGrants })}
            />
          </div>
        </>
      )}
    </>
  );

  // Object type selection for adding new objects
  const ObjectTypeSelector = (
    <>
      <div className="col-span-2">
        <DropdownSingleselect
          label="Object Type"
          options={['table', 'view', 'procedure', 'function', 'file_format', 'sequence']}
          selected={objectType ?? ''}
          setSelected={(value: string) => setObjectType(value as IObjectType)}
        />
      </div>
    </>
  );

  const onSubmit = (): void => {
    if (props.config?.action === 'add' && objectType !== undefined && objectType !== 'database') {
      let objectData = null;
      switch (objectType) {
        case 'schema':
          objectData = schemaConfig;
          break;
        case 'table':
          objectData = tableConfig;
          break;
        case 'view':
          objectData = viewConfig;
          break;
        case 'procedure':
          objectData = procedureConfig;
          break;
        case 'function':
          objectData = functionConfig;
          break;
        case 'file_format':
          objectData = fileFormatConfig;
          break;
        case 'sequence':
          objectData = sequenceConfig;
          break;
        case 'column':
          objectData = columnConfig;
          break;
      }

      if (objectData !== null) {
        props.onAddObject(objectType, objectData);
      }
    } else if (props.config?.action === 'edit' && objectType !== undefined) {
      let objectData = null;
      switch (objectType) {
        case 'database':
          objectData = databaseConfig;
          break;
        case 'schema':
          objectData = schemaConfig;
          break;
        case 'table':
          objectData = tableConfig;
          break;
        case 'view':
          objectData = viewConfig;
          break;
        case 'procedure':
          objectData = procedureConfig;
          break;
        case 'function':
          objectData = functionConfig;
          break;
        case 'file_format':
          objectData = fileFormatConfig;
          break;
        case 'sequence':
          objectData = sequenceConfig;
          break;
        case 'column':
          objectData = columnConfig;
          break;
      }

      if (objectData !== null) {
        const originalName =
          props.config?.objectData !== undefined &&
          typeof (props.config.objectData as { name: string }).name === 'string'
            ? (props.config.objectData as { name: string }).name
            : '';
        props.onEditObject(objectType, objectData, originalName);
      }
    }
  };

  return (
    <Transition.Root show={props.open} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" initialFocus={cancelButtonRef} onClose={props.onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="w-[35rem] 2xl:w-[45rem] wide:w-[50rem] relative transform overflow-visible rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:p-6">
                <div className="text-left">
                  <DialogTitle as="h3" className="text-xl text-center font-semibold leading-6 text-gray-900">{`${
                    props.config?.action === 'add' ? 'Add' : 'Edit'
                  } ${(objectType ?? 'object') as string}`}</DialogTitle>
                  <Description className="mb-4 text-center text-sm text-gray-600">
                    Configure the {objectType ?? 'object'} properties
                  </Description>
                  <div className="w-full grid grid-cols-2 gap-4">
                    {props.config?.action === 'add' && props.config?.objectType === undefined && ObjectTypeSelector}
                    {objectType === 'database' && DatabaseFields}
                    {objectType === 'schema' && SchemaFields}
                    {objectType === 'table' && TableFields}
                    {objectType === 'view' && ViewFields}
                    {objectType === 'procedure' && ProcedureFields}
                    {objectType === 'function' && FunctionFields}
                    {objectType === 'file_format' && FileFormatFields}
                    {objectType === 'sequence' && SequenceFields}
                    {objectType === 'column' && ColumnFields}
                  </div>
                </div>
                <div className="mt-6 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    className="flex-1 mt-3 inline-flex w-full justify-center rounded-md bg-dataops-primary px-3 py-2 text-sm font-semibold text-gray-50 shadow-sm hover:bg-dataops-primary-hover sm:mt-0 sm:w-auto disabled:bg-gray-400"
                    onClick={() => onSubmit()}
                    disabled={
                      objectType === undefined ||
                      (objectType === 'database' && (databaseConfig === null || databaseConfig.name === '')) ||
                      (objectType === 'schema' && (schemaConfig === null || schemaConfig.name === '')) ||
                      (objectType === 'table' && (tableConfig === null || tableConfig.name === '')) ||
                      (objectType === 'view' && (viewConfig === null || viewConfig.name === '')) ||
                      (objectType === 'procedure' && (procedureConfig === null || procedureConfig.name === '')) ||
                      (objectType === 'function' && (functionConfig === null || functionConfig.name === '')) ||
                      (objectType === 'file_format' &&
                        (fileFormatConfig === null ||
                          fileFormatConfig.name === '' ||
                          fileFormatConfig.format_type === '')) ||
                      (objectType === 'sequence' && (sequenceConfig === null || sequenceConfig.name === '')) ||
                      (objectType === 'column' &&
                        (columnConfig === null || columnConfig.name === '' || columnConfig.type === ''))
                    }
                  >
                    {`${props.config?.action === 'add' ? 'Add' : 'Update'} ${(objectType ?? 'object') as string}`}
                  </button>
                  <button
                    type="button"
                    className="flex-1 mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    ref={cancelButtonRef}
                    onClick={props.onClose}
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
